"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, MapPin, CircleCheck as CheckCircle2, ArrowRight, Building2, ChartBar as BarChart3, ShieldCheck, Target, Zap, Quote } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
const caseStudies = [{
  id: "wiltshire-services",
  location: "Swindon & Wiltshire",
  category: "Local Service SEO",
  title: "+184% Organic Search Traffic for Wiltshire Advisory Firm",
  subtitle: "Dominating local high-intent search queries through entity-focused local schema & technical optimization.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_wiltshire.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_wiltshire.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_wiltshire.png-wvc-sizes",
  altText: "SEO growth analytics graph showing traffic increase for Wiltshire client",
  metric1: "+184%",
  metric1Label: "Organic Traffic Growth",
  metric2: "#1 - #3",
  metric2Label: "Google 3-Pack Rankings",
  metric3: "42",
  metric3Label: "Monthly Extra Leads",
  challenge: "The client was struggling with low visibility in Google Map Packs across Swindon and Wiltshire, relying heavily on expensive paid search ads for high-value legal and advisory enquiries.",
  strategy: "I re-architected their site around high-intent local entity clusters, implemented hyper-local schema markup, and optimized their Google Business Profile signals for core service keywords.",
  implementation: "Audited and resolved NAP inconsistencies, created dedicated location landing pages with authentic local copy, and conducted a targeted technical speed and crawlability overhaul.",
  results: "Achieved top 3 Google Map Pack positions for 24 targeted service terms within 6 months, increasing total organic impressions by over 220%.",
  impact: "Generated 42 additional qualified inbound enquiries every month, allowing the firm to reduce PPC ad spend by 35% while expanding their team.",
  quote: "Working directly with Yassine gave us complete clarity. He cut through the usual agency fluff and delivered measurable local growth.",
  clientName: "Managing Partner",
  clientRole: "Wiltshire Advisory Firm"
}, {
  id: "thames-valley-b2b",
  location: "Thames Valley Region",
  category: "Multi-Location SEO",
  title: "310% Map Pack Impression Surge Across 5 Commercial Hubs",
  subtitle: "Consolidating regional multi-branch search authority for a Thames Valley commercial B2B provider.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_thames.png",
  imageSrcSet: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_thames.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/case_study_thames.png-wvc-sizes",
  altText: "Local search map pack rankings and local search visibility overview",
  metric1: "310%",
  metric1Label: "Map Pack Impressions",
  metric2: "Top 3",
  metric2Label: "For 38 Target Terms",
  metric3: "2.4x",
  metric3Label: "Phone Call Volume",
  challenge: "Severe keyword cannibalisation across 5 regional hub pages meant the business was losing local market share to smaller competitors in key commercial towns.",
  strategy: "I developed a streamlined location hierarchy, eliminating internal competition and establishing clear geographic entity boundaries for search engine crawlers.",
  implementation: "Optimized individual Google Business Profiles, cleaned up structured local citations, and established contextual backlink acquisition from verified regional sources.",
  results: "310% surge in local Google Map Pack impressions and top 3 organic placements across Reading, Oxford, and surrounding commercial hubs.",
  impact: "Inbound telephone leads grew 2.4x, establishing the business as the dominant regional supplier without increasing marketing overheads.",
  quote: "Yassine's hands-on approach meant no account managers or miscommunication—just direct access to an expert who knows local search inside out.",
  clientName: "Operations Director",
  clientRole: "Thames Valley B2B Logistics"
}];
const trustSignals = [{
  iconName: "ShieldCheck",
  title: "100% Direct Access",
  description: "You work directly with me, Yassine Essaadi. No account managers, junior handlers, or outsourced tasks."
}, {
  iconName: "Target",
  title: "Tailored Strategy",
  description: "Bespoke local search plans built specifically for your location, target audience, and business goals."
}, {
  iconName: "BarChart3",
  title: "Transparent Metrics",
  description: "Clear reporting on real business outcomes—rankings, qualified organic traffic, and inbound leads."
}];
export default function LocalImpact() {
  const [activeTab, setActiveTab] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const selectedCase = caseStudies[activeTab];
  return <section id="localimpactcasestudies" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-medium px-3 py-1">
            <ShieldCheck className="w-3.5 h-3.5 text-primary mr-1" />
            Verified Local Impact
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-sans text-foreground mb-4">
            Genuine Local SEO Case Studies
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg font-default leading-relaxed">
            I work directly with UK businesses to build real, sustainable search visibility. Here are verified
            results from my hands-on local SEO strategies—structured by Challenge, Strategy, Implementation, and Impact.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-10">
          {caseStudies.map((study, index) => <Button key={study.id} data-index={index} variant={activeTab === index ? "default" : "outline"} size="lg" onClick={() => setActiveTab(index)} className="rounded-full transition-all duration-200">
              <Building2 className="w-4 h-4 mr-2" />
              <span>{study.location}</span>
              <span className="text-xs opacity-80 hidden sm:inline">({study.category})</span>
            </Button>)}
        </div>

        <motion.div key={selectedCase.id} initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="mb-16">
          <Card className="border border-border/80 bg-card shadow-lg rounded-2xl overflow-hidden">
            <div className="bg-primary text-primary-foreground p-6 sm:p-8 lg:p-10">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="secondary" className="bg-primary-foreground/15 text-primary-foreground border-none">
                      <MapPin className="w-3 h-3 mr-1" />
                      {selectedCase.location}
                    </Badge>
                    <span className="text-primary-foreground/80 text-xs sm:text-sm font-medium">
                      {selectedCase.category}
                    </span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-semibold font-sans leading-tight">
                    {selectedCase.title}
                  </h3>
                  <p className="text-primary-foreground/90 text-sm sm:text-base mt-2 font-default">
                    {selectedCase.subtitle}
                  </p>
                </div>

                <div className="flex items-center">
                  <Button asChild variant="secondary" size="lg" className="bg-background text-foreground hover:bg-background/90 shadow-md rounded-xl font-medium">
                    <Link to="/CaseStudiesPage">
                      Explore All Case Studies
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>

              <Separator className="my-8 bg-primary-foreground/20" />

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-primary-foreground/10 backdrop-blur-xs rounded-xl p-4 border border-primary-foreground/10 text-center sm:text-left">
                  <div className="text-3xl sm:text-4xl font-bold font-sans text-primary-foreground">
                    {selectedCase.metric1}
                  </div>
                  <div className="text-primary-foreground/80 text-xs sm:text-sm mt-1 font-medium">
                    {selectedCase.metric1Label}
                  </div>
                </div>
                <div className="bg-primary-foreground/10 backdrop-blur-xs rounded-xl p-4 border border-primary-foreground/10 text-center sm:text-left">
                  <div className="text-3xl sm:text-4xl font-bold font-sans text-primary-foreground">
                    {selectedCase.metric2}
                  </div>
                  <div className="text-primary-foreground/80 text-xs sm:text-sm mt-1 font-medium">
                    {selectedCase.metric2Label}
                  </div>
                </div>
                <div className="bg-primary-foreground/10 backdrop-blur-xs rounded-xl p-4 border border-primary-foreground/10 text-center sm:text-left">
                  <div className="text-3xl sm:text-4xl font-bold font-sans text-primary-foreground">
                    {selectedCase.metric3}
                  </div>
                  <div className="text-primary-foreground/80 text-xs sm:text-sm mt-1 font-medium">
                    {selectedCase.metric3Label}
                  </div>
                </div>
              </div>
            </div>

            <CardContent className="p-6 sm:p-8 lg:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
                  <div className="rounded-xl overflow-hidden border border-border shadow-sm relative group">
                    <img src={selectedCase.imageSrc} data-wvc-srcset={selectedCase.imageSrcSet} data-wvc-sizes={selectedCase.imageSizes} alt={selectedCase.altText} loading="lazy" className="w-full h-64 sm:h-80 lg:h-96 object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent flex items-end p-6">
                      <div className="text-background">
                        <span className="text-xs uppercase tracking-wider font-semibold opacity-90 block">Verified Outcome</span>
                        <span className="text-sm font-medium">{selectedCase.location} Client Partner</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-muted/60 p-6 rounded-xl border border-border/60 relative">
                    <Quote className="w-8 h-8 text-accent/40 absolute top-4 right-4" />
                    <p className="text-foreground text-sm sm:text-base italic font-default relative z-10 mb-4">
                      "{selectedCase.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <SafeHydrate><Avatar className="h-10 w-10 border border-primary/20">
                        <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_avatar.webp" alt="Yassine Essaadi - SEO Consultant" />
                        <AvatarFallback className="bg-primary text-primary-foreground font-semibold text-xs">
                          YE
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{selectedCase.clientName}</div>
                        <div className="text-xs text-muted-foreground">{selectedCase.clientRole}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 space-y-6">
                  <div>
                    <h4 className="text-lg font-semibold font-sans text-foreground flex items-center gap-2 mb-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-destructive/10 text-destructive text-xs font-bold">1</span>
                      The Challenge
                    </h4>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8">
                      {selectedCase.challenge}
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-lg font-semibold font-sans text-foreground flex items-center gap-2 mb-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">2</span>
                      The Tailored Strategy
                    </h4>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8">
                      {selectedCase.strategy}
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-lg font-semibold font-sans text-foreground flex items-center gap-2 mb-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-accent/20 text-accent-foreground text-xs font-bold">3</span>
                      Practical Implementation
                    </h4>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8">
                      {selectedCase.implementation}
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-lg font-semibold font-sans text-foreground flex items-center gap-2 mb-2">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-chart-4/20 text-chart-4 text-xs font-bold">4</span>
                      Verified Results
                    </h4>
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-8">
                      {selectedCase.results}
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-lg font-semibold font-sans text-foreground flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-primary" />
                      Business Impact
                    </h4>
                    <p className="text-foreground font-medium text-sm sm:text-base leading-relaxed pl-8 bg-secondary/50 p-4 rounded-lg border border-secondary">
                      {selectedCase.impact}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className="bg-muted/40 rounded-2xl p-6 sm:p-8 border border-border/60">
          <div className="text-center max-w-xl mx-auto mb-8">
            <h3 className="text-xl sm:text-2xl font-semibold font-sans text-foreground mb-2">
              Why Work Directly With Me?
            </h3>
            <p className="text-sm text-muted-foreground">
              I deliver high-performing SEO strategies without agency markups, junior staff handoffs, or hidden processes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trustSignals.map((signal, index) => <Card key={index} data-index={index} className="bg-card border-border/50 shadow-xs">
                <CardHeader className="pb-2">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary mb-3">
                    {signal.iconName === "ShieldCheck" && <ShieldCheck className="w-5 h-5" />}
                    {signal.iconName === "Target" && <Target className="w-5 h-5" />}
                    {signal.iconName === "BarChart3" && <BarChart3 className="w-5 h-5" />}
                  </div>
                  <CardTitle className="text-base font-semibold font-sans">{signal.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                    {signal.description}
                  </CardDescription>
                </CardContent>
              </Card>)}
          </div>

          <div className="mt-8 text-center">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 rounded-xl font-medium shadow-md">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}